import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Plus, UserMinus, Users } from "lucide-mui";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { type ProspectListContactEntry, api } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { ORG_CRM_READ_SCOPE } from "../../../auth/orgRoutes.ts";
import { userHasOrgScope } from "../../../auth/scopes.ts";
import AddContactDialog from "../../../components/AddContactDialog.tsx";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import EmptyState from "../../../components/EmptyState.tsx";
import Panel from "../../../components/Panel.tsx";
import ReviewToggle from "../../../components/ReviewToggle.tsx";
import RowActions from "../../../components/RowActions.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../../components/filters/useFilters.ts";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import { useContactEntryMutations } from "../../../components/prospects/collection/useContactEntryMutations.ts";
import { useCrmStatus } from "../../../components/prospects/collection/useCrmStatus.ts";
import { getProspectStatus } from "../../../components/prospects/collection/entryStatus.ts";
import ContactNoteCell from "../../../components/prospects/collection/ContactNoteCell.tsx";
import CrmStatusCell from "../../../components/prospects/collection/CrmStatusCell.tsx";
import { formatNumber } from "../../../utils/format.ts";

// Search only.
const NO_FILTERS: FilterSpec[] = [];

function reachabilityLine(e: ProspectListContactEntry): string {
  return [e.reachability.email, e.reachability.phone, e.reachability.linkedin_url]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Flat view of every contact on the list. Contacts bound to a company are also
 * reachable by expanding that company row; contacts with no company live only
 * here.
 */
export default function ProspectContactsPanel({ listId }: { listId: string }) {
  const [addOpen, setAddOpen] = useState(false);
  const filterState = useLocalFilterState();
  const filters = useFilters(NO_FILTERS, {
    state: filterState,
    search: { param: "q", placeholder: "Search name, company, email…" },
  });
  const search = filterState.searchParams.get("q") ?? "";
  const { tenantPath } = useTenant();
  const { user } = useAuth();
  const { selectedOrgId } = useOrganization();
  // CRM reads are 403'd for members without the CRM reader role.
  const canReadCrm = userHasOrgScope(user, selectedOrgId, ORG_CRM_READ_SCOPE);

  const contacts = useQuery({
    queryKey: keys.prospectListContactEntry.byList(listId),
    queryFn: () => api.getProspectContacts(listId),
  });

  const mutations = useContactEntryMutations(listId);
  const crm = useCrmStatus(listId, canReadCrm);

  const rows = useMemo(() => {
    const all = contacts.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((e) =>
      [e.contact_name, e.title, e.company_name, reachabilityLine(e)]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [contacts.data, search]);

  const total = contacts.data?.length ?? 0;
  const colSpan = crm.available ? 8 : 7;

  return (
    <Panel
      title="All contacts"
      count={total}
      disablePadding
      action={
        <Button variant="outlined" size="small" startIcon={<Plus />} onClick={() => setAddOpen(true)}>
          Add contact
        </Button>
      }
    >
      {contacts.isLoading ? (
        <CenteredSpinner py={6} />
      ) : total === 0 ? (
        <EmptyState
          icon={<Users />}
          title="No contacts in this collection yet"
          description="Add one from a company row, or use “Add contact”: matches are checked to avoid duplicates."
        />
      ) : (
        <>
          <FilterBar
            filters={filters}
            count={
              filters.activeCount > 0
                ? `${formatNumber(rows.length)} of ${formatNumber(total)}`
                : `${formatNumber(total)} ${total === 1 ? "contact" : "contacts"}`
            }
          />
          <ScrollTable sx={{ "& .MuiTableCell-root": { py: 0.25, px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Reachability</TableCell>
                <TableCell>Company</TableCell>
                {crm.available && <TableCell>CRM</TableCell>}
                <TableCell sx={{ minWidth: 180 }}>Note</TableCell>
                <TableCell align="right">Status</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e) => {
                const status = getProspectStatus(e);
                const reach = reachabilityLine(e);
                return (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {e.contact_name ?? "(no name)"}
                        </Typography>
                        {status !== "disqualified" && e.missing_required_properties.length > 0 && (
                          <Tooltip title={`Missing required: ${e.missing_required_properties.join(", ")}`}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                cursor: "help",
                                color: "warning.main",
                              }}
                            >
                              <AlertTriangle fontSize="small" />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {e.title ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {reach || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {e.master_company_id ? (
                        <Link
                          component={RouterLink}
                          to={tenantPath(`/companies/${e.master_company_id}`)}
                          underline="hover"
                        >
                          {e.company_name ?? "(unnamed company)"}
                        </Link>
                      ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          — unassigned
                        </Typography>
                      )}
                    </TableCell>
                    {crm.available && (
                      <TableCell>
                        <CrmStatusCell status={crm.byEntry.get(e.id)} />
                      </TableCell>
                    )}
                    <TableCell>
                      <ContactNoteCell
                        entry={e}
                        onSave={(note) => mutations.updateNote.mutate({ entryId: e.id, note })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <ReviewToggle value={status} onChange={(next) => mutations.setStatus(e.id, status, next)} />
                    </TableCell>
                    <TableCell padding="checkbox" align="right">
                      <RowActions
                        label={e.contact_name ?? "contact"}
                        actions={[
                          {
                            label: "Remove from collection",
                            icon: <UserMinus fontSize="small" />,
                            destructive: true,
                            onClick: () => mutations.remove.mutateAsync(e.id),
                            confirm: {
                              title: `Remove ${e.contact_name ?? "this contact"} from the collection?`,
                              body: "The person stays in the database; only their entry in this collection goes.",
                              confirmLabel: "Remove",
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <EmptyTableRow colSpan={colSpan}>No contacts match your search</EmptyTableRow>
              )}
            </TableBody>
          </ScrollTable>
        </>
      )}

      {addOpen && <AddContactDialog onClose={() => setAddOpen(false)} listId={listId} />}
    </Panel>
  );
}
